/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import { toast } from 'react-toastify';
import Cookies from "js-cookie";
import { TENANT_COMPANY_MAP } from '../../../utils/constants';
import { request } from '../../../config/helpers/axios-instance';

/**
 * Queue every item on this order that has no pill images yet with the tenant's MDM agent.
 *
 * Eazy's MDM sits inside hospital infrastructure and cannot accept inbound connections, so
 * images cannot be fetched on demand the way the per-item "MDM Images" button does for
 * PillUp. Instead the ids go into a queue that the MDM machine polls, and the images appear
 * on the item within a minute or so. Refresh the page to see them.
 */
export default function RequestMdmImagesButton({ Items }) {
    const tenantId = Cookies.get("tenantId");
    const isEazyTenant = tenantId === TENANT_COMPANY_MAP["EAZY"];
    const [requesting, setRequesting] = useState(false);

    // The MDM keys images off the mnemonic, which is what the per-item fetch sends too.
    // Composite keys (containing ':') are timing rows rather than real items.
    const missingImageMnemonics = Object.keys(Items || {})
        .filter((key) => !key.includes(':'))
        .filter((key) => !Items[key]?.item?.pillImages?.length)
        .map((key) => Items[key]?.item?.mnemonic)
        .filter(Boolean);

    const uniqueMnemonics = [...new Set(missingImageMnemonics)];

    if (!isEazyTenant || !uniqueMnemonics.length) return null;

    const requestImages = async () => {
        try {
            setRequesting(true);
            const response = await request({
                url: '/mdm/requestImages',
                method: 'POST',
                data: { medicineIds: uniqueMnemonics }
            });

            if (response?.data?.success) {
                toast.success(`Requested images for ${uniqueMnemonics.length} medicine(s). They will appear shortly.`);
            } else {
                toast.error('Failed to request images');
            }
        } catch (error) {
            toast.error('Failed to request images');
        } finally {
            setRequesting(false);
        }
    };

    return (
        <div className='flex items-center justify-between gap-4 p-3 bg-indigo-50 border border-indigo-200 rounded-xl text-sm'>
            <span className='text-indigo-900'>
                {uniqueMnemonics.length} medicine{uniqueMnemonics.length > 1 ? 's have' : ' has'} no images yet.
            </span>
            <button
                className='px-3 py-2 rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-xs font-medium whitespace-nowrap'
                onClick={requestImages}
                disabled={requesting}
                title="Ask the MDM machine to send images for these medicines"
            >
                {requesting ? 'Requesting...' : 'Request Images'}
            </button>
        </div>
    )
}
